import { uniq } from 'lodash';
import type { Organisation } from './types';

type OrganisationInheritanceSource = Pick<
  Organisation,
  'id' | 'parentId' | 'type' | 'projectAccesses' | 'features' | 'labFeatures'
> &
  Partial<Pick<Organisation, 'samplingPointAccesses' | 'sampleAccesses'>>;

export type OrganisationInheritanceSnapshot = Pick<
  Organisation,
  'projectAccesses' | 'samplingPointAccesses' | 'sampleAccesses' | 'features' | 'labFeatures'
>;

export function buildOrganisationInheritanceSnapshotById(
  organisations: readonly OrganisationInheritanceSource[],
): ReadonlyMap<number, OrganisationInheritanceSnapshot> {
  const organisationById = new Map(organisations.map(org => [org.id, org] as const));
  const snapshotById = new Map<number, OrganisationInheritanceSnapshot>();
  const resolving = new Set<number>();

  const resolve = (organisationId: number): OrganisationInheritanceSnapshot | undefined => {
    const cached = snapshotById.get(organisationId);
    if (cached) {
      return cached;
    }

    const organisation = organisationById.get(organisationId);
    if (!organisation) {
      return undefined;
    }

    if (resolving.has(organisationId)) {
      const cycleSnapshot = {
        projectAccesses: uniq(organisation.projectAccesses),
        samplingPointAccesses: uniq(organisation.samplingPointAccesses ?? []),
        sampleAccesses: uniq(organisation.sampleAccesses ?? []),
        features: uniq(organisation.features),
        labFeatures: uniq(organisation.labFeatures),
      };
      snapshotById.set(organisationId, cycleSnapshot);
      return cycleSnapshot;
    }

    resolving.add(organisationId);

    const parent =
      organisation.parentId == null ? undefined : organisationById.get(Number(organisation.parentId)) ?? undefined;

    const parentSnapshot = parent && parent.type === organisation.type ? resolve(parent.id) : undefined;

    const snapshot = parentSnapshot
      ? {
          projectAccesses: uniq([...organisation.projectAccesses, ...parentSnapshot.projectAccesses]),
          samplingPointAccesses: uniq([
            ...(organisation.samplingPointAccesses ?? []),
            ...parentSnapshot.samplingPointAccesses,
          ]),
          sampleAccesses: uniq([...(organisation.sampleAccesses ?? []), ...parentSnapshot.sampleAccesses]),
          features: uniq([...organisation.features, ...parentSnapshot.features]),
          labFeatures: uniq([...organisation.labFeatures, ...parentSnapshot.labFeatures]),
        }
      : {
          projectAccesses: uniq(organisation.projectAccesses),
          samplingPointAccesses: uniq(organisation.samplingPointAccesses ?? []),
          sampleAccesses: uniq(organisation.sampleAccesses ?? []),
          features: uniq(organisation.features),
          labFeatures: uniq(organisation.labFeatures),
        };

    resolving.delete(organisationId);
    snapshotById.set(organisationId, snapshot);
    return snapshot;
  };

  organisations.forEach(organisation => {
    resolve(organisation.id);
  });

  return snapshotById;
}
